import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { ResumeParserAction } from "@/types";

const parseProfileFromTextSchema = z.object({
  resumeText: z.string().min(1, "Required"),
});

export const ParseFromResumeTextDialog = ({
  mode,
  onOpenChange,
  open,
  userId,
}: {
  mode: ResumeParserAction;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  userId: number;
}) => {
  const {
    mutate: parseResumeTextToUser,
    isPending: isPendingParseResumeTextToUser,
  } = novaClient.internal.user.active.parseTextToProfile.useMutation();
  const novaClientUtils = novaClient.useUtils();

  const form = useForm<z.infer<typeof parseProfileFromTextSchema>>({
    defaultValues: { resumeText: "" },
    resolver: zodResolver(parseProfileFromTextSchema),
  });

  const onSuccess = () => {
    // The sheet renders the profile from this query; the parse rewrote it.
    void novaClientUtils.internal.user.active.getCandidacySheetData.invalidate();
    form.reset();
    onOpenChange(false);
  };

  const handleSubmit = (data: z.infer<typeof parseProfileFromTextSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Parse from text",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        mode,
      },
    );
    if (mode === ResumeParserAction.OVERWRITE) {
      mixpanelUtils.trackButtonClick(
        "Overwrite Profile - Parse From Text",
        MIXPANEL_SCREENS.CANDIDATE_SHEET,
      );
    }
    parseResumeTextToUser(
      {
        action: mode,
        resumeText: data.resumeText,
        userId,
      },
      {
        onSuccess,
      },
    );
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            {mode === ResumeParserAction.PARSE_AND_RECONCILE
              ? "Add to profile from resume text"
              : "Overwrite profile from resume text"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {mode === ResumeParserAction.OVERWRITE && (
              <Text className="text-sm font-normal text-red-600">
                This will replace the candidate&apos;s entire profile with data
                parsed from the resume text. This action cannot be undone.
              </Text>
            )}
            <FormField
              control={form.control}
              name="resumeText"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Resume Text</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={20} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPendingParseResumeTextToUser}
                type="submit"
                variant={
                  mode === ResumeParserAction.OVERWRITE
                    ? "destructive"
                    : "default"
                }
              >
                {mode === ResumeParserAction.OVERWRITE ? "Overwrite" : "Parse"}
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
